import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
  Modal,
  Button,
} from '@mui/material';
import VehicleInspections from '../api/vehicle_inspections';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import CustomSnackbar from '../../components/CustomSnackbar';
import InspectionHistoryTable from '../components/InspectionHistoryTable';
import ConsultationWalletCard from '../components/ConsultationWalletCard';
import PackagePurchase from '../components/PackagePurchase';
import { format, isAfter, subMinutes, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const MyInspections = () => {
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [inspections, setinspections] = useState([]);
  const [error, setError] = useState(null);
  const scrollContainerRef = useRef(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const cardsPerView = isSmallScreen ? 1 : 2;
  const navigate = useNavigate();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;
  const location = useLocation();
  const [openModal, setOpenModal] = useState(Boolean(location.state?.warning));
  const [successMessage] = useState(location.state?.success || null);

  const handleSelectPackage = (pkg) => {
    navigate(ROUTES.PACKAGE_PAYMENT, { state: { package: pkg } });
  };
  
  const handleNext = () => {
    if (scrollContainerRef.current) {
      const newSlide = Math.min(currentSlide + 1, totalSlides - 1);
      setCurrentSlide(newSlide);
      const slideWidth = scrollContainerRef.current.offsetWidth;
      scrollContainerRef.current.scrollTo({
        left: newSlide * slideWidth,
        behavior: 'smooth'
      });
    }
  };

  const handlePrev = () => {
    if (scrollContainerRef.current) {
      const newSlide = Math.max(currentSlide - 1, 0);
      setCurrentSlide(newSlide);
      const slideWidth = scrollContainerRef.current.offsetWidth;
      scrollContainerRef.current.scrollTo({
        left: newSlide * slideWidth,
        behavior: 'smooth'
      });
    }
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const slideWidth = scrollContainerRef.current.offsetWidth;
      const newSlide = Math.round(scrollContainerRef.current.scrollLeft / slideWidth);
      setCurrentSlide(newSlide);
    }
  };

  const handleInspectionClick = (inspection) => {    
    const id = inspection.id;

    if (!id) {
      setOpenSnackbar(true);
      return;
    }

    navigate(`${ROUTES.MY_INSPECTIONS}/${id}`);
  };

  const getValidScheduledInspections = (inspections) => {
    if (!inspections) return [];
    
    const currentDate = new Date();
    const cutoffDate = subMinutes(currentDate, 30);

    const filtered = inspections.filter(inspection => {
      if (!inspection.presential_scheduling_datetime) {
        return false;
      }
      
      const schedulingDate = parseISO(inspection.presential_scheduling_datetime);
      
      const isValid = isAfter(schedulingDate, cutoffDate);
      
      return isValid;
    });
    
    const formatted = filtered.map(inspection => {
      const schedulingDate = parseISO(inspection.presential_scheduling_datetime);
      return {
        id: inspection.id,
        day: format(schedulingDate, 'dd'),
        month: format(schedulingDate, 'MMM', { locale: ptBR }).toUpperCase(),
        address: inspection.appointment_address || 'Endereço não disponível',
        fullDate: format(schedulingDate, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR }),
        time: format(schedulingDate, "HH'h'mm")
      };
    });

    return formatted;
  };

  const validScheduledInspections = getValidScheduledInspections(inspections);
  const totalSlides = Math.ceil(validScheduledInspections.length / cardsPerView);

  const hasValidScheduling = inspections?.some(inspection => {
    if (!inspection.presential_scheduling_datetime) return false;
    
    const schedulingDate = parseISO(inspection.presential_scheduling_datetime);
    const currentDate = new Date();
    const cutoffDate = subMinutes(currentDate, 30);
    
    return isAfter(schedulingDate, cutoffDate);
  });

  const getCurrentPageItems = () => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return inspections.slice(startIndex, endIndex);
  };

  useEffect(() => {
    if (inspections?.length) {
      setTotalPages(Math.ceil(inspections.length / itemsPerPage));
    }
  }, [inspections]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    window.scrollTo({
      top: document.getElementById('history-section').offsetTop,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const fetchInspections = async () => {
      try {
        setLoading(true);
        const data = await VehicleInspections.getAll();
        const list = Array.isArray(data) ? data : (data?.inspections || []);
        const normalized = list
          .map((i) => ({
            ...i,
            value: Number(i.value ?? i.plan_value ?? 0),
            inspection_status: i.inspection_status || 'awaiting_payment',
          }))
          .sort((a, b) => {
            const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
            return dateB - dateA;
          });
        setinspections(normalized);
      } catch (err) {
        console.error('Erro ao carregar inspeções:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInspections();
  }, []);

  const handleClose = () => {
    setOpenModal(false);
    navigate(location.pathname, { 
      replace: true,
      state: {} 
    });
  };

  return (
    <Box 
      sx={{ 
        bgcolor: '#F5F7F9',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Container 
        maxWidth="xl" 
        sx={{ 
          flex: 1,
          py: 2,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box sx={{ py: 4, flex: 1 }}>
          {loading ? (
            <Typography align="center">Carregando...</Typography>
          ) : error ? (
            <Typography align="center">Erro: {error}</Typography>
          ) : (
            <>
              <Typography 
                variant="h4" 
                component="h1" 
                align="center" 
                sx={{ 
                  mb: 4, 
                  fontWeight: 600,
                  color: '#012F47'
                }}
              >
                Minhas Vistorias
              </Typography>

              {/* Carteira de consultas B2B + compra de pacote (renderiza apenas para usuários empresa) */}
              <Box sx={{ maxWidth: '1200px', mx: 'auto', px: { xs: 2, md: 4 } }}>
                {successMessage ? (
                  <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: '#E8F7EF', color: '#00B26C', fontWeight: 600 }}>
                    {successMessage}
                  </Box>
                ) : null}
                <ConsultationWalletCard />
                <PackagePurchase onSelectPackage={handleSelectPackage} />
              </Box>
            
              {/* Commented out to hide scheduled inspections section
              {hasValidScheduling && (
                <Box sx={{ 
                  maxWidth: '1200px',
                  mx: 'auto',
                  px: { xs: 2, md: 4 }
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ fontWeight: 500 }}
                  >
                    Agendadas:
                  </Typography>
                  
                  <Box sx={{ position: 'relative' }}>
                    {validScheduledInspections.length > 2 && (
                      <>
                        {currentSlide > 0 && (
                          <IconButton
                            onClick={handlePrev}
                            sx={{
                              position: 'absolute',
                              left: { xs: -20, md: -28 },
                              top: '40%',
                              transform: 'translateY(-50%)',
                              bgcolor: 'white',
                              boxShadow: 2,
                              width: 40,
                              height: 40,
                              '&:hover': { 
                                bgcolor: 'grey.100',
                                boxShadow: 3 
                              },
                              zIndex: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <KeyboardArrowLeftIcon sx={{ fontSize: 24 }} />
                          </IconButton>
                        )}
                        
                        {currentSlide < totalSlides - 1 && (
                          <IconButton
                            onClick={handleNext}
                            sx={{
                              position: 'absolute',
                              right: { xs: -20, md: -28 },
                              top: '40%',
                              transform: 'translateY(-50%)',
                              bgcolor: 'white',
                              boxShadow: 2,
                              width: 40,
                              height: 40,
                              '&:hover': { 
                                bgcolor: 'grey.100',
                                boxShadow: 3 
                              },
                              zIndex: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <KeyboardArrowRightIcon sx={{ fontSize: 24 }} />
                          </IconButton>
                        )}
                      </>
                    )}

                    <Box
                      ref={scrollContainerRef}
                      onScroll={handleScroll}
                      sx={{
                        display: 'flex',
                        gap: 3,
                        overflowX: 'hidden',
                        scrollSnapType: 'x mandatory',
                        '&::-webkit-scrollbar': { display: 'none' },
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                        position: 'relative',
                        mx: { xs: 2, md: 3 },
                        py: 2,
                        px: 1
                      }}
                    >
                      {validScheduledInspections.map((inspection) => {
                        return (
                          <Card 
                            key={inspection.id} 
                            sx={{ 
                              flex: { 
                                xs: '0 0 100%',
                                sm: '0 0 100%',
                                md: '0 0 calc(50% - 12px)',
                                lg: `0 0 calc(50% - 12px)` 
                              },
                              scrollSnapAlign: 'start',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: 4
                              },
                              m: 1,
                              maxWidth: {
                                xs: '100%',
                                sm: '450px',
                                md: '400px',
                                lg: 'none'
                              },
                              mx: {
                                xs: 0,
                                sm: 'auto',
                                md: 0,
                                lg: 0
                              }
                            }}
                          >
                            <CardContent sx={{ 
                              display: 'flex', 
                              p: { xs: 2, sm: 3 },
                              '&:last-child': { pb: { xs: 2, sm: 3 } }
                            }}>
                              <Paper 
                                elevation={0}
                                sx={{
                                  width: { xs: 65, sm: 75 },
                                  height: { xs: 65, sm: 75 },
                                  bgcolor: 'white',
                                  color: '#012F47',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: 2,
                                  border: '2px solid #012F47'
                                }}
                              >
                                <Typography 
                                  variant="h4" 
                                  sx={{ 
                                    fontWeight: 700, 
                                    color: '#012F47',
                                    lineHeight: 1,
                                    pt: 0.7,
                                    fontSize: { xs: '1.8rem', sm: '2.125rem' }
                                  }}
                                >
                                  {inspection.day}
                                </Typography>
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    fontWeight: 700,
                                    color: '#012F47',
                                    mt: -0.5,
                                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                  }}
                                >
                                  {inspection.month}
                                </Typography>
                              </Paper>

                              <Box sx={{ 
                                ml: 3, 
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <Box 
                                    component="img" 
                                    src={LocationIcon} 
                                    alt="Localização"
                                    sx={{ 
                                      width: 18,
                                      height: 18,
                                      mr: 1,
                                      flexShrink: 0
                                    }}
                                  />
                                  <Typography 
                                    variant="body2" 
                                    color="text.primary"
                                    sx={{ 
                                      lineHeight: 1.4,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      fontWeight: 400
                                    }}
                                  >
                                    {inspection.address}
                                  </Typography>
                                </Box>
                                
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box 
                                      component="img" 
                                      src={CalendarIcon} 
                                      alt="Calendário"
                                    />
                                    <Typography variant="caption">
                                      {inspection.fullDate}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box 
                                      component="img" 
                                      src={ClockIcon} 
                                      alt="Relógio"
                                      sx={{ 
                                        width: 18,
                                        height: 18,
                                        mr: 1,
                                        flexShrink: 0
                                      }}
                                    />
                                    <Typography variant="caption">
                                      {inspection.time}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>

                    {validScheduledInspections.length > 2 && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: 1,
                          mt: 3
                        }}
                      >
                        {Array.from({ length: totalSlides }).map((_, index) => (
                          <Box
                            key={index}
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: currentSlide === index ? '#012F47' : 'grey.300',
                              transition: 'background-color 0.3s',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              setCurrentSlide(index);
                              const slideWidth = scrollContainerRef.current?.offsetWidth || 0;
                              scrollContainerRef.current?.scrollTo({
                                left: index * slideWidth,
                                behavior: 'smooth'
                              });
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              )} */}

              <Box 
                id="history-section"
                sx={{ 
                  maxWidth: '1200px',
                  mx: 'auto',
                  px: { xs: 2, md: 4 },
                  mt: 6
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 3 }}>
                  Histórico:
                </Typography>

                <InspectionHistoryTable
                  inspections={inspections}
                  pageItems={getCurrentPageItems()}
                  onRowClick={handleInspectionClick}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  paginationSize={isMobile ? 'small' : 'medium'}
                />
              </Box>
            </>
          )}
        </Box>
      </Container>

      <CustomSnackbar
        open={openSnackbar}
        onClose={() => setOpenErrorSnackbar(false)}
        message="Não foi possível encontrar a placa do veículo"
        severity="error"
      />

      <Modal
        open={openModal}
        onClose={handleClose}
        aria-labelledby="warning-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '450px' },
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          textAlign: 'center',
        }}>
          <WarningAmberIcon 
            sx={{ 
              fontSize: 64,
              color: '#FFA500',
              mb: 2
            }}
          />
          
          <Typography 
            id="warning-modal-title"
            variant="h6"
            component="h2"
            sx={{ 
              mb: 2,
              fontWeight: 600,
              color: '#012F47'
            }}
          >
            {location.state?.warningTitle || "Aviso"}
          </Typography>
          
          <Typography 
            sx={{ 
              mb: 3,
              color: '#536777'
            }}
          >
            {location.state?.warning}
          </Typography>

          <Button
            variant="contained"
            onClick={handleClose}
            sx={{
              bgcolor: '#51DF94',
              '&:hover': {
                bgcolor: '#00B26C',
              },
              textTransform: 'none',
              px: 4,
              py: 1.5,
            }}
          >
            Entendi
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default MyInspections;
